import React, { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Tab, Tabs } from 'material-ui/Tabs'
import {
  fetchMessageSettings,
  fetchSocialLinks,
} from '../../state/actions/message-settings'
import { fetchApptTypes } from '../../state/actions/appointment-types'
import HorizontalRule from '../sitewide/HorizontalRule'
import MessageSettings from './MessageSettings'
import OpenChairMessageEditor from './OpenChairMessageEditor'
import NewAppointmentMessageEditor from './NewAppointmentMessageEditor'
import ExistingPatientMessageEditor from './ExistingPatientMessageEditor'
import PostApptReviewMessageEditor from './PostApptReviewMessageEditor'
import MissedCallMessageSetting from './MissedCallMessageSetting'
import TabStyles from './styles/Tabs'
import WeeklyHours from '../sitewide/WeeklyHours'

class Settings extends Component {
  static propTypes = {
    activeLocation: PropTypes.shape({
      active: PropTypes.bool.isRequired,
      additional_recipients: PropTypes.arrayOf(PropTypes.object),
      city: PropTypes.string,
      country_code: PropTypes.string,
      created_at: PropTypes.string,
      doctor_id: PropTypes.string,
      earliest_same_day_reminder_hour: PropTypes.number,
      facebook_url: PropTypes.string,
      forwarding_email: PropTypes.string,
      google_url: PropTypes.string,
      id: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      name: PropTypes.string,
      office_email: PropTypes.string,
      office_phone: PropTypes.string,
      phone_numbers: PropTypes.arrayOf(PropTypes.object),
      practice_id: PropTypes.number,
      state: PropTypes.string,
      street_address: PropTypes.string,
      timezone: PropTypes.string,
      updated_at: PropTypes.string,
      website: PropTypes.string,
      yelp_url: PropTypes.string,
      zip: PropTypes.string,
    }),
    getSettings: PropTypes.func.isRequired,
    getSocLinks: PropTypes.func.isRequired,
    hasDSO: PropTypes.bool,
    hasNewPatientOS: PropTypes.bool,
    hasPatientPortal: PropTypes.bool,
    hasReminders: PropTypes.bool,
    hasReviews: PropTypes.bool,
    hasTextingOnly: PropTypes.bool,
    hasTopsRemindersPlus: PropTypes.bool.isRequired,
    loadApptTypes: PropTypes.func.isRequired,
    messageSettingsById: PropTypes.objectOf(
      PropTypes.shape({
        active: PropTypes.bool.isRequired,
        always_send: PropTypes.bool.isRequired,
        created_at: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
        message_setting_type: PropTypes.string.isRequired,
        message_type: PropTypes.string.isRequired,
        offset: PropTypes.number.isRequired,
        practice_id: PropTypes.number.isRequired,
        updated_at: PropTypes.string.isRequired,
      }),
    ),
    newApptSettings: PropTypes.objectOf(
      PropTypes.shape({
        active: PropTypes.bool.isRequired,
        always_send: PropTypes.bool.isRequired,
        created_at: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
        message_setting_type: PropTypes.string.isRequired,
        message_type: PropTypes.string.isRequired,
        offset: PropTypes.number.isRequired,
        practice_id: PropTypes.number.isRequired,
        updated_at: PropTypes.string.isRequired,
      }),
    ),
    missedCallSettings: PropTypes.objectOf(
      PropTypes.shape({
        active: PropTypes.bool.isRequired,
        always_send: PropTypes.bool.isRequired,
        created_at: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
        message_setting_type: PropTypes.string.isRequired,
        message_type: PropTypes.string.isRequired,
        offset: PropTypes.number.isRequired,
        practice_id: PropTypes.number.isRequired,
        updated_at: PropTypes.string.isRequired,
      }),
    ),
    pmsType: PropTypes.string.isRequired,
    practiceName: PropTypes.string.isRequired,
    reviewSettings: PropTypes.objectOf(
      PropTypes.shape({
        active: PropTypes.bool.isRequired,
        always_send: PropTypes.bool.isRequired,
        created_at: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
        message_setting_type: PropTypes.string.isRequired,
        message_type: PropTypes.string.isRequired,
        offset: PropTypes.number.isRequired,
        practice_id: PropTypes.number.isRequired,
        updated_at: PropTypes.string.isRequired,
      }),
    ),
    schedulingSettings: PropTypes.objectOf(
      PropTypes.shape({
        active: PropTypes.bool.isRequired,
        always_send: PropTypes.bool.isRequired,
        created_at: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
        message_setting_type: PropTypes.string.isRequired,
        message_type: PropTypes.string.isRequired,
        offset: PropTypes.number.isRequired,
        practice_id: PropTypes.number.isRequired,
        updated_at: PropTypes.string.isRequired,
      }),
    ),
    maxChars: PropTypes.number.isRequired,
    hasScreenPop: PropTypes.bool,
  }

  state = {
    currentSelectedTab: 'reminders',
    isPopoverOpen: false,
  }

  async componentDidMount() {
    const { activeLocation, getSettings, getSocLinks, hasDSO, loadApptTypes } =
      this.props
    if (hasDSO) {
      const { id } = activeLocation
      await getSettings(id)
      await getSocLinks(id)
    } else {
      await getSettings()
      await getSocLinks()
    }
    loadApptTypes()
    this.checkTabs()
  }

  async componentWillReceiveProps({ activeLocation: nextActiveLocation }) {
    const { activeLocation, getSettings, getSocLinks, hasDSO } = this.props
    if (hasDSO) {
      const { id: nextId } = nextActiveLocation
      const { id } = activeLocation
      if (nextId !== id) {
        await getSettings(nextId)
        await getSocLinks(nextId)
      }
    }
  }

  checkTabs = () => {
    const { hasNewPatientOS, hasPatientPortal, hasReminders } = this.props
    if (!hasReminders) {
      const tab =
        !hasPatientPortal && !hasNewPatientOS ? 'reviews' : 'onlineScheduling'
      this.toggleTabs(tab)
    }
  }

  togglePopover = isPopoverOpen => {
    this.setState({ isPopoverOpen })
  }

  filterMessageSettings = typeToFilterOn => {
    const { messageSettingsById } = this.props
    return Object.values(messageSettingsById).filter(
      messageSetting => messageSetting.message_setting_type === typeToFilterOn,
    )
  }

  toggleTabs = value => {
    if (typeof value !== 'string') return
    this.setState({ currentSelectedTab: value })
    this.togglePopover(false)
  }

  render() {
    const {
      activeLocation,
      hasNewPatientOS,
      hasPatientPortal,
      hasReminders,
      hasReviews,
      hasTextingOnly,
      hasTopsRemindersPlus,
      maxChars,
      newApptSettings: { newApptEmail, newApptText },
      missedCallSettings: { missedCallText, missedCallEmail },
      pmsType,
      practiceName,
      reviewSettings,
      schedulingSettings: { openchairEmail, openchairText, patientPortalEmail },
      hasScreenPop,
    } = this.props
    const { currentSelectedTab, isPopoverOpen } = this.state
    const showOpenChairMessages =
      hasNewPatientOS && (!!openchairEmail || !!openchairText)
    const showExistingPatientMessages = hasPatientPortal && !!patientPortalEmail
    const showPostApptReviews =
      (hasReviews || hasTopsRemindersPlus) &&
      !!Object.keys(reviewSettings).length
    const missedCallTextEligiblePractices = ['Rooster Grin Media', 'Efros Orthodontics', 'Mattiacio Orthodontics', 'Ricci Orthodontic Studio', 'Depew Orthodontics', 'TW Orthodontics', 'CB Orthodontics', 'Wait Orthodontic Specialists']
    const showMissedCallText = (hasScreenPop || hasTextingOnly) && missedCallTextEligiblePractices.includes(practiceName)
    const newApptMessageEligiblePMSes = ['Cloud9']
    const newApptMessageEligiblePracs = {
      development: ['Cloud9 Sandbox'],
      staging: ['Cloud9 Test Practice'],
      production: ['Cloud9 Test Practice', 'Specialty Smile Partners, LLC'],
    }
    const newApptMessageEligible =
      hasReminders &&
      newApptMessageEligiblePMSes.includes(pmsType) &&
      newApptMessageEligiblePracs[process.env.REACT_APP_ENV].includes(
        practiceName,
      )
    const isTops = !!process.env.REACT_APP_IS_TOPS
    const pmsStyles = TabStyles(isTops)
    const tabStylesArr = [
      'reminders',
      'newAppointments',
      'onlineScheduling',
      'reviews',
      'voip',
      'missedCallText'
    ]
    const tabStyles = tabStylesArr.reduce(
      (acc, tab) => ({
        ...acc,
        [`${tab}`]:
          tab === currentSelectedTab ? pmsStyles.activeTab : pmsStyles.tab,
      }),
      {},
    )

    const onlyReminders =
      hasReminders &&
      !hasTopsRemindersPlus &&
      !showOpenChairMessages &&
      !showExistingPatientMessages &&
      !showPostApptReviews &&
      !showMissedCallText

    const onlyOpenChair =
      !hasReminders &&
      !hasTopsRemindersPlus &&
      showOpenChairMessages &&
      !showExistingPatientMessages &&
      !showPostApptReviews &&
      !showMissedCallText

    const onlyExistingPatient =
      !hasReminders &&
      !hasTopsRemindersPlus &&
      !showOpenChairMessages &&
      showExistingPatientMessages &&
      !showPostApptReviews &&
      !showMissedCallText

    const onlyReviews =
      !hasReminders &&
      !hasTopsRemindersPlus &&
      !showOpenChairMessages &&
      !showExistingPatientMessages &&
      showPostApptReviews &&
      !showMissedCallText

    const onlyMissedCallText =
      !hasReminders &&
      !hasTopsRemindersPlus &&
      !showOpenChairMessages &&
      !showExistingPatientMessages &&
      !showPostApptReviews &&
      showMissedCallText

    switch (true) {
      case onlyReminders:
        return (
          <div style={{ textAlign: 'center' }}>
            <MessageSettings
              isPopoverOpen={isPopoverOpen}
              togglePopover={this.togglePopover}
              isTops={isTops}
            />
          </div>
        )
      case onlyOpenChair: {
        return (
          <div style={{ textAlign: 'center' }}>
            <OpenChairMessageEditor
              openchairEmail={openchairEmail}
              openchairText={openchairText}
              maxChars={maxChars}
            />
          </div>
        )
      }
      case onlyReviews:
        return (
          <div style={{ textAlign: 'center' }}>
            <PostApptReviewMessageEditor
              hasReviews={hasReviews || hasTopsRemindersPlus}
              locationId={activeLocation ? activeLocation.id : null}
              maxChars={maxChars}
              postApptReviewMessages={reviewSettings}
            />
          </div>
        )
      case onlyExistingPatient: {
        return (
          <div style={{ textAlign: 'center' }}>
            <ExistingPatientMessageEditor
              existingPatientMessage={patientPortalEmail}
            />
          </div>
        )
      }
      case onlyMissedCallText: {
        return (
          <div style={{ textAlign: 'center' }}>
            <MissedCallMessageSetting
              // missedCallEmail={missedCallEmail}
              missedCallText={missedCallText}
            />
          </div>
        )
      }
      default:
        return (
          <Tabs
            value={currentSelectedTab}
            onChange={this.toggleTabs}
            inkBarStyle={pmsStyles.underline}
          >
            {hasReminders && (
              <Tab
                label="Reminders"
                value="reminders"
                style={tabStyles.reminders}
              >
                <div style={{ textAlign: 'center' }}>
                  <MessageSettings
                    activeLocation={activeLocation}
                    isPopoverOpen={isPopoverOpen}
                    togglePopover={this.togglePopover}
                  />
                </div>
              </Tab>
            )}
            {newApptMessageEligible && (
              <Tab
                label="New Appointments"
                value="newAppointments"
                style={tabStyles.newAppointments}
              >
                <div style={{ textAlign: 'center' }}>
                  <NewAppointmentMessageEditor
                    newApptEmail={newApptEmail}
                    newApptText={newApptText}
                    maxChars={maxChars}
                  />
                </div>
              </Tab>
            )}
            {(showOpenChairMessages || showExistingPatientMessages) && (
              <Tab
                label="Online Scheduling"
                value="onlineScheduling"
                style={tabStyles.onlineScheduling}
              >
                <div style={{ textAlign: 'center' }}>
                  {showOpenChairMessages && (
                    <OpenChairMessageEditor
                      openchairEmail={openchairEmail}
                      openchairText={openchairText}
                      maxChars={maxChars}
                    />
                  )}
                  {showExistingPatientMessages && (
                    <div>
                      {showOpenChairMessages && <HorizontalRule />}
                      <ExistingPatientMessageEditor
                        existingPatientMessage={patientPortalEmail}
                      />
                    </div>
                  )}
                </div>
              </Tab>
            )}
            {showPostApptReviews && (
              <Tab label="Reviews" value="reviews" style={tabStyles.reviews}>
                <div style={{ textAlign: 'center' }}>
                  <PostApptReviewMessageEditor
                    locationId={activeLocation ? activeLocation.id : null}
                    hasReviews={hasReviews || hasTopsRemindersPlus}
                    maxChars={maxChars}
                    postApptReviewMessages={reviewSettings}
                  />
                </div>
              </Tab>
            )}
            {showMissedCallText && (
              <Tab label="Missed Call Text" value="missedCallText" style={tabStyles.missedCallText}>
                <div style={{ textAlign: 'center' }}>
                  <MissedCallMessageSetting
                    // missedCallEmail={missedCallEmail}
                    missedCallText={missedCallText}
                  />
                  <WeeklyHours />
                </div>
              </Tab>
            )}
          </Tabs>
        )
    }
  }
}

const mapStateToProps = ({
  messageSettings: {
    byId: messageSettingsById,
    missedCallSettings,
    newApptSettings,
    reviewSettings,
    schedulingSettings,
  },
  account: {
    user: { activeLocation, practiceName, countryCode },
    auth: {
      hasDSO,
      hasNewPatientOS,
      hasPatientPortal,
      hasReminders,
      hasReviews,
      hasTextingOnly,
      hasTopsRemindersPlus,
      pmsType,
      hasScreenPop,
    },
  },
}) => {
  const maxChars = ['US', 'CA'].includes(countryCode) ? 460 : 300
  return {
    activeLocation,
    hasDSO,
    hasNewPatientOS,
    hasPatientPortal,
    hasReminders,
    hasTopsRemindersPlus,
    hasReviews,
    hasTextingOnly,
    maxChars,
    messageSettingsById,
    newApptSettings,
    missedCallSettings,
    pmsType,
    practiceName,
    reviewSettings,
    schedulingSettings,
    hasScreenPop,
  }
}

const mapDispatchToProps = {
  getSettings: loc => fetchMessageSettings(loc),
  getSocLinks: loc => fetchSocialLinks(loc),
  loadApptTypes: () => fetchApptTypes(true),
}

export default connect(mapStateToProps, mapDispatchToProps)(Settings)



// WEBPACK FOOTER //
// ./src/components/Settings/index.js